import {
  ChevronRightIcon,
  InfoOutlineIcon,
  WarningTwoIcon,
} from '@chakra-ui/icons';
import { Flex, Tooltip } from '@chakra-ui/react';
import type { ComponentProps } from 'react';

import { CheckboxIndicator, Option, RadioIndicator, Text } from '@revelio/ui';

type DataBuilderOptionProps = Omit<ComponentProps<typeof Option>, 'cssVars'>;

const DataBuilderOptionBase = ({ style, ...props }: DataBuilderOptionProps) => {
  return (
    <Option
      style={{
        flex: 1,
        minHeight: '3rem',
        alignItems: 'flex-start',
        ...style,
      }}
      cssVars={{ '--ui-option-padding': '0.225rem 0.875rem' }}
      {...props}
    />
  );
};

const CheckBox = ({ isSelected }: { isSelected: boolean }) => {
  return (
    <Flex alignItems="center" justifyContent="center">
      <CheckboxIndicator checked={isSelected} />
    </Flex>
  );
};

const Chevron = () => {
  return (
    <ChevronRightIcon
      boxSize="18px"
      color="text.primary"
      opacity={0.5}
      flexShrink={0}
    />
  );
};

const Description = ({ children }: { children: string }) => {
  return (
    <Text fontSize="x-sm" style={{ opacity: 0.7 }}>
      {children}
    </Text>
  );
};

const DescriptionTooltip = ({ description }: { description: string }) => {
  return (
    <Tooltip label={description} textAlign="center" hasArrow placement="top">
      <InfoOutlineIcon
        boxSize="14px"
        color="text.primary"
        opacity={0.7}
        cursor="pointer"
        flexShrink={0}
      />
    </Tooltip>
  );
};

const WarningTooltip = ({
  message,
  testId,
}: {
  message: string;
  testId?: string;
}) => {
  return (
    <Tooltip label={message} textAlign="center" hasArrow placement="top">
      <WarningTwoIcon
        data-testid={testId}
        boxSize="14px"
        color="orange.400"
        cursor="pointer"
        flexShrink={0}
      />
    </Tooltip>
  );
};

const Label = ({ children }: { children: string }) => {
  return (
    <Text
      fontSize="md"
      style={{ marginBottom: '0.125rem', lineHeight: '1.05' }}
    >
      {children}
    </Text>
  );
};

const Radio = ({ isSelected }: { isSelected: boolean }) => {
  return <RadioIndicator checked={isSelected} style={{ marginTop: '4px' }} />;
};

export const DataBuilderOption = Object.assign(DataBuilderOptionBase, {
  CheckBox,
  Chevron,
  Description,
  DescriptionTooltip,
  Label,
  Radio,
  WarningTooltip,
});
